import React, { FC } from 'react';
import { useSelector } from 'react-redux';

import ButtonSelectShow from 'components/button/buttonSelectShow/ButtonSelectShow';
import Dropdown from 'components/form/dropdown/Dropdown';
import Input from 'components/form/input/Input';

import { RootState } from 'store/rootState';

import { playbackModesForIndex } from './Event.helpers';
import { EventShow, ShowPlaybackMode } from './Event.types';
import { MAX_SHOW_TIME_SECONDS } from './Event.validation';

interface EventShowItemErrors {
  time?: string;
  repetition?: string;
}

interface EventShowItemProps {
  show: EventShow;
  index: number;
  showCount: number;
  disabled?: boolean;
  pendingApproval?: boolean;
  errors?: EventShowItemErrors;
  onRemove: () => void;
  onTimeChange: (value: string) => void;
  onRepetitionChange: (value: string) => void;
  onPlaybackModeChange: (value: ShowPlaybackMode) => void;
}

const playbackModeLabels: { [key in ShowPlaybackMode]: string } = {
  [ShowPlaybackMode.REPETITION]: 'Repetition',
  [ShowPlaybackMode.TIME]: 'Time',
  [ShowPlaybackMode.LOOPING]: 'Looping',
};

const EventShowItem: FC<EventShowItemProps> = ({
  show,
  index,
  showCount,
  disabled = false,
  pendingApproval = false,
  errors,
  onRemove,
  onTimeChange,
  onRepetitionChange,
  onPlaybackModeChange,
}) => {
  const arrangement = useSelector((state: RootState) =>
    state.arrangements.arrangements.find(
      item => item.id === show.arrangementId,
    ),
  );
  const playbackModes = playbackModesForIndex(index, showCount);

  return (
    <div className="event-show-item">
      <ButtonSelectShow
        selected
        disabled={disabled}
        pendingApproval={pendingApproval}
        arrangement={arrangement ?? { name: '', active: false }}
        onClearSelection={onRemove}
      >
        <Dropdown
          label="Playback"
          name={`shows[${index}].playbackMode`}
          id={`shows-${index}-playback-mode`}
          purpose="navigate"
          width={160}
          values={playbackModes.map(mode => ({
            label: playbackModeLabels[mode],
            value: mode,
          }))}
          value={show.playbackMode}
          hasEmptyOption={false}
          onChange={(_name, value) => onPlaybackModeChange(value)}
          disabled={disabled || showCount === 1}
        />

        {show.playbackMode === ShowPlaybackMode.TIME && (
          <Input
            type="number"
            name={`shows[${index}].time`}
            id={`shows-${index}-time`}
            label={'\u00a0'}
            aria-label="Time"
            scheme="white"
            className="button-select-show--selected__timing-input"
            placeholder="0"
            suffix="second(s)"
            min={1}
            max={MAX_SHOW_TIME_SECONDS}
            value={show.time}
            onChange={event => onTimeChange(event.target.value)}
            error={errors?.time}
            disabled={disabled}
          />
        )}

        {show.playbackMode === ShowPlaybackMode.REPETITION && (
          <Input
            type="number"
            name={`shows[${index}].repetition`}
            id={`shows-${index}-repetition`}
            label={'\u00a0'}
            aria-label="Repetition"
            scheme="white"
            className="button-select-show--selected__timing-input"
            placeholder="1"
            suffix="time(s)"
            min={1}
            max={100}
            value={show.repetition}
            onChange={event => onRepetitionChange(event.target.value)}
            error={errors?.repetition}
            disabled={disabled}
          />
        )}
      </ButtonSelectShow>
    </div>
  );
};

export default EventShowItem;
